/**
 * Group Quiz Join Page
 * Mobile-friendly page for participants to join a group quiz
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import GroupQuizClient from '../components/GroupQuizClient';
import './groupQuizJoin.css';

interface GroupQuiz {
    id: number;
    hash: string;
    quizId: number;
    quizTitle: string;
    hostUsername: string;
    dateTaken: string;
    dateCompleted: string | null;
    userNames: string[];
    whoWon: string | null;
}

export default function GroupQuizJoin() {
    const { hash } = useParams<{ hash: string }>();
    const navigate = useNavigate();
    const [groupQuiz, setGroupQuiz] = useState<GroupQuiz | null>(null);
    const [username, setUsername] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [joining, setJoining] = useState(false);
    const [joined, setJoined] = useState(false);
    const [cancelled, setCancelled] = useState(false);
    const [quizStarted, setQuizStarted] = useState(false);

    // Fetch group quiz details
    useEffect(() => {
        const fetchGroupQuiz = async () => {
            try {
                const response = await fetch(`/api/group-quiz/${hash}`);
                if (!response.ok) {
                    throw new Error('Group quiz not found');
                }

                const data = await response.json();
                setGroupQuiz(data.groupQuiz);
                setLoading(false);
            } catch (err: any) {
                setError(err.message);
                setLoading(false);
            }
        };

        fetchGroupQuiz();
    }, [hash]);

    // Setup WebSocket to listen for quiz cancellation
    useEffect(() => {
        if (!hash || !joined) return;

        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl = `${protocol}//${window.location.host}/ws`;
        const websocket = new WebSocket(wsUrl);

        websocket.onopen = () => {
            console.log('WebSocket connected for user:', username);
            websocket.send(JSON.stringify({
                type: 'join-room',
                roomId: hash,
                username: username,
            }));
        };

        websocket.onmessage = (event) => {
            const data = JSON.parse(event.data);

            if (data.type === 'quiz-cancelled') {
                setCancelled(true);
            }

            if (data.type === 'quiz-started') {
                setQuizStarted(true);
            }
        };

        websocket.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        websocket.onclose = () => {
            console.log('WebSocket disconnected');
        };

        return () => {
            websocket.close();
        };
    }, [hash, joined, username]);

    const handleJoin = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!username.trim()) {
            setError('Please enter a username');
            return;
        }

        setJoining(true);
        setError(null);

        try {
            const response = await fetch(`/api/group-quiz/${hash}/join`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username: username.trim() }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to join quiz');
            }

            setJoined(true);
        } catch (err: any) {
            setError(err.message);
            setJoining(false);
        }
    };

    if (loading) {
        return (
            <div className="groupQuizJoinContainer">
                <div className="loading">Loading...</div>
            </div>
        );
    }

    if (error && !groupQuiz) {
        return (
            <div className="groupQuizJoinContainer">
                <div className="errorCard">
                    <h2>Error</h2>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    if (!groupQuiz) {
        return (
            <div className="groupQuizJoinContainer">
                <div className="errorCard">
                    <h2>Quiz Not Found</h2>
                    <p>The quiz you're looking for doesn't exist.</p>
                </div>
            </div>
        );
    }

    if (groupQuiz.dateCompleted) {
        return (
            <div className="groupQuizJoinContainer">
                <div className="errorCard">
                    <h2>Quiz Completed</h2>
                    <p>This quiz has already been completed.</p>
                </div>
            </div>
        );
    }

    if (cancelled) {
        return (
            <div className="groupQuizJoinContainer">
                <div className="errorCard">
                    <h2>Quiz Cancelled</h2>
                    <p>The host has cancelled this quiz.</p>
                </div>
            </div>
        );
    }

    if (joined) {
        // If quiz has started, show the quiz client
        if (quizStarted && hash) {
            return <GroupQuizClient hash={hash} username={username} />;
        }

        // Otherwise show waiting screen
        return (
            <div className="groupQuizJoinContainer">
                <div className="successCard">
                    <div className="successIcon">✓</div>
                    <h2>You're In!</h2>
                    <p className="welcomeText">Welcome, <strong>{username}</strong>!</p>
                    <p className="waitText">
                        You've successfully joined <strong>{groupQuiz.quizTitle}</strong>.
                    </p>
                    <p className="waitText">
                        Waiting for <strong>{groupQuiz.hostUsername}</strong> to start the quiz...
                    </p>
                    <div className="loadingSpinner"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="groupQuizJoinContainer">
            <div className="card">
                <div className="header">
                    <h1>{groupQuiz.quizTitle}</h1>
                    <p className="hostInfo">
                        Hosted by <strong>{groupQuiz.hostUsername}</strong>
                    </p>
                </div>

                <div className="participantsInfo">
                    <p>{groupQuiz.userNames.length} participant{groupQuiz.userNames.length !== 1 ? 's' : ''} joined</p>
                </div>

                <form onSubmit={handleJoin} className="form">
                    <div className="formGroup">
                        <label htmlFor="username">Enter Your Name</label>
                        <input
                            type="text"
                            id="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="Your name"
                            disabled={joining}
                            autoFocus
                            maxLength={30}
                        />
                    </div>

                    {error && <div className="error">{error}</div>}

                    <button
                        type="submit"
                        className="joinButton"
                        disabled={joining || !username.trim()}
                    >
                        {joining ? 'Joining...' : 'Join Quiz'}
                    </button>
                </form>
            </div>
        </div>
    );
}
